/**
 * API Client
 * Custom fetch wrapper for API calls
 * Used by Orval-generated hooks
 */

import { API_BASE, IS_DEV, DEV_TEAM_ID } from '@/lib/env'
import { validationDetailMessage } from '@/api/errorDetail'
import { getAccessToken } from '@/contexts/AuthContext'
import { notifyIfSessionExpired } from '@/lib/sessionExpiry'
import { ACTIVE_TEAM_KEY, IMPERSONATE_TEAM_KEY, readTabScope } from '@/lib/tabScope'

export interface ApiErrorDetail {
  message?: string
  code?: string
  provider?: string
}

export interface ApiError {
  // FastAPI request-validation errors (422) arrive as a LIST of entries.
  detail: string | ApiErrorDetail | unknown[]
  status?: number
}

export class ApiRequestError extends Error {
  code?: string
  provider?: string
  status?: number

  constructor(message: string, code?: string, provider?: string, status?: number) {
    super(message)
    this.name = 'ApiRequestError'
    this.code = code
    this.provider = provider
    this.status = status
  }
}

/**
 * The error type of every orval-generated hook. Orval looks for an export
 * named exactly `ErrorType` in the mutator file (override.mutator in
 * orval.config.ts) and writes `TError = ErrorType<HTTPValidationError>`
 * into the generated hooks. `orvalFetch` throws an ApiRequestError on every
 * non-2xx status, so that is what a hook's `error` is; the schema's 422
 * body never reaches a caller as a thrown value.
 */
export type ErrorType<_Body> = ApiRequestError

/** HTTP status of a thrown API error, if it was an ApiRequestError. */
export function getApiErrorStatus(error: unknown): number | undefined {
  return error instanceof ApiRequestError ? error.status : undefined
}

/** Backend `detail` string of a thrown API error, if it was an ApiRequestError. */
export function getApiErrorDetail(error: unknown): string | undefined {
  return error instanceof ApiRequestError ? error.message : undefined
}

/**
 * The error a failed response becomes.
 *
 * A 401 also wakes the sign-in redirect, so an expired session sends the user
 * back to Keycloak instead of leaving a load error on whichever page happened
 * to fetch next. lib/sessionExpiry.ts explains why 401 alone means that.
 */
async function apiError(response: Response): Promise<ApiRequestError> {
  notifyIfSessionExpired(response.status)

  // A non-JSON body is a Cloudflare-wrapped 5xx or an HTML error page.
  // Keep the status visible so it is distinguishable from a client error.
  const error = (await response.json().catch(() => ({
    detail: `Request failed (HTTP ${response.status.toString()})`,
  }))) as ApiError

  const validationMessage = validationDetailMessage(error.detail)
  if (validationMessage !== undefined) {
    return new ApiRequestError(validationMessage, undefined, undefined, response.status)
  }

  // Structured error details (e.g. { message, code, provider })
  if (typeof error.detail === 'object' && !Array.isArray(error.detail)) {
    const detail = error.detail
    return new ApiRequestError(
      detail.message || 'Request failed',
      detail.code,
      detail.provider,
      response.status
    )
  }

  return new ApiRequestError(
    typeof error.detail === 'string' && error.detail !== ''
      ? error.detail
      : `API error: ${response.status.toString()}`,
    undefined,
    undefined,
    response.status
  )
}

export interface OrvalRequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean | null | undefined | string[]>
  data?: unknown
}

/**
 * The team this tab's requests act on (lib/tabScope.ts), or the dev team in
 * local development before one is chosen.
 */
export function getActiveTeamId(): string | null {
  return readTabScope(ACTIVE_TEAM_KEY) ?? (IS_DEV ? DEV_TEAM_ID : null)
}

/**
 * The team this tab is impersonating (lib/tabScope.ts), or null.
 *
 * Sent on every request. No browser route is exempt: the one Settings
 * section that shows the signed-in user's own data (Teams) reads
 * /api/auth/me, which never re-scopes, and its team delete is keyed by the
 * caller rather than the team header. The registry in
 * components/settings/sections.ts records which section is which, and
 * ImpersonationBanner explains it on the page.
 */
export function getImpersonateTeamId(): string | null {
  return readTabScope(IMPERSONATE_TEAM_KEY)
}

/**
 * An API path as an absolute URL with its query params, arrays repeating
 * their key. The page's own origin is the base when API_BASE is empty
 * (relative URLs in K8s).
 */
export function apiUrl(url: string, params?: OrvalRequestConfig['params']): string {
  const fullUrl = new URL(url, API_BASE || window.location.origin)
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return
    if (Array.isArray(value)) {
      value.forEach((item) => {
        fullUrl.searchParams.append(key, item)
      })
    } else {
      fullUrl.searchParams.append(key, typeof value === 'string' ? value : value.toString())
    }
  })
  return fullUrl.toString()
}

/**
 * The headers every API request carries: the bearer token, the tab's team,
 * and during staff impersonation the impersonated team. The backend
 * re-scopes an impersonated request to that team (read-only, staff-only,
 * audited) and rejects non-staff callers.
 */
export function apiHeaders(init?: HeadersInit): Headers {
  const headers = new Headers(init)
  const token = getAccessToken()
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  const teamId = getActiveTeamId()
  if (teamId) {
    headers.set('X-Team-Id', teamId)
  }
  const impersonateTeamId = getImpersonateTeamId()
  if (impersonateTeamId) {
    headers.set('X-Impersonate-Team', impersonateTeamId)
  }
  return headers
}

/**
 * Custom fetch wrapper for Orval-generated API calls
 * Handles JSON serialization, error handling, and authentication
 *
 * Orval v8+ calls mutators with two arguments: (url, options)
 *
 * @param url - The API endpoint URL
 * @param config - Request configuration with options
 * @returns Parsed JSON response
 * @throws Error with detail from API or generic error message
 */
export async function customFetch<T>(
  url: string,
  config?: Omit<OrvalRequestConfig, 'url'>
): Promise<T> {
  const { params, data, ...options } = config ?? {}
  const headers = apiHeaders(options.headers)

  // Handle request body
  let body = options.body
  if (data) {
    // If data is FormData, don't set Content-Type (browser will set it with boundary)
    if (data instanceof FormData) {
      body = data
      // Remove any Content-Type header - browser must set it with the boundary
      headers.delete('Content-Type')
    } else {
      // For JSON data
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(data)
    }
  } else if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(apiUrl(url, params), {
    ...options,
    body,
    headers,
  })

  if (!response.ok) {
    throw await apiError(response)
  }

  // Handle no-content responses
  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined as T
  }

  return response.json() as Promise<T>
}

/**
 * A file download (the audit log's CSV export): the same headers as every
 * API call, and the Response itself back, so the caller reads the body as
 * a blob and the headers the export sets. A failed status throws the same
 * ApiRequestError the JSON calls do.
 */
export async function fetchDownload(
  url: string,
  params?: OrvalRequestConfig['params']
): Promise<Response> {
  const response = await fetch(apiUrl(url, params), { headers: apiHeaders() })
  if (!response.ok) {
    throw await apiError(response)
  }
  return response
}

/**
 * Orval-shaped fetch wrapper.
 *
 * Orval v8's FETCH client generates response types as
 * `{ data, status, headers }`. Returning just the parsed body (as
 * `customFetch` does for direct call sites) would make orval-generated
 * hooks type-lie at runtime. This mutator returns the wrapped shape orval
 * expects so generated `use*` hooks work without consumer-side casts.
 *
 * Direct (non-orval) call sites should continue to use `customFetch`.
 */
export async function orvalFetch<T>(
  url: string,
  config?: Omit<OrvalRequestConfig, 'url'>
): Promise<T> {
  const { params, data, ...options } = config ?? {}
  const headers = apiHeaders(options.headers)

  let body = options.body
  if (data) {
    if (data instanceof FormData) {
      body = data
      headers.delete('Content-Type')
    } else {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(data)
    }
  } else if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await fetch(apiUrl(url, params), {
    ...options,
    body,
    headers,
  })

  if (!response.ok) {
    throw await apiError(response)
  }

  // No-content responses still need to satisfy orval's wrapped shape.
  const noContent =
    response.status === 204 || response.headers.get('Content-Length') === '0'
  const responseData: unknown = noContent ? undefined : await response.json()

  return {
    data: responseData as never,
    status: response.status,
    headers: response.headers,
  } as T
}
